import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Package, Ruler, Scissors, User, Trash2, Plus, DollarSign, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/options";
import { RcButton } from "@/components/RcButton";

const STATUS_OPTIONS = ["draft","ordered","paid","in_production","shipped","delivered","cancelled"];
const PAYMENT_STATUS_OPTIONS = ["pending","unpaid","paid","refunded","failed","cancelled"];

const StatCard = ({ label, value, testid }) => (
  <div className="border border-[#362B28] bg-[#1F1816] p-6 rounded-sm">
    <div className="text-xs tracking-[0.3em] uppercase text-[#A39E93]">{label}</div>
    <div className="font-display text-4xl text-[#CFA05E] mt-3" data-testid={testid}>{value}</div>
  </div>
);

const Row = ({ label, value }) => (
  <>
    <div className="text-xs tracking-[0.2em] uppercase text-[#A39E93] self-center">{label}</div>
    <div className="capitalize">{value}</div>
  </>
);

/* ---------------- Order Modal ---------------- */
function OrderModal({ order, onClose, onSaved }) {
  const init = PAYMENT_STATUS_OPTIONS.includes(order.payment_status) ? order.payment_status : "pending";
  const [paymentStatus, setPaymentStatus] = useState(init);
  const [amount, setAmount] = useState(order.amount);
  const [notes, setNotes] = useState(order.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.session_id}`, {
        payment_status: paymentStatus,
        amount: parseFloat(amount),
        notes,
      });
      toast.success("Order updated");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#120D0C]/85 backdrop-blur flex items-start justify-center overflow-y-auto p-6" onClick={onClose}>
      <div className="w-full max-w-2xl bg-[#1F1816] border border-[#362B28] rounded-sm my-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#362B28]">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E]">Order</div>
            <div className="font-display text-2xl mt-1">{order.design?.name || "—"}</div>
          </div>
          <button onClick={onClose} data-testid="close-order-modal" className="text-[#A39E93] hover:text-[#FDFBF7]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-xs tracking-[0.2em] uppercase text-[#A39E93] mb-1">Customer</div><div>{order.user_email}</div></div>
            <div><div className="text-xs tracking-[0.2em] uppercase text-[#A39E93] mb-1">Session</div><div className="font-mono text-xs break-all">{order.session_id}</div></div>
            <div><div className="text-xs tracking-[0.2em] uppercase text-[#A39E93] mb-1">Created</div><div>{new Date(order.created_at).toLocaleString()}</div></div>
            <div><div className="text-xs tracking-[0.2em] uppercase text-[#A39E93] mb-1">Currency</div><div className="uppercase">{order.currency}</div></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Amount</label>
              <input type="number" step="0.01" data-testid="order-amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-4 py-3 text-sm" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Payment Status</label>
              <select data-testid="order-payment-status" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-4 py-3 text-sm uppercase tracking-wider">
                {PAYMENT_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">Notes</label>
            <textarea rows={3} data-testid="order-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-4 py-3 text-sm" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#362B28]">
            <RcButton variant="outline" onClick={onClose}>Cancel</RcButton>
            <RcButton onClick={save} disabled={saving} data-testid="save-order-btn">{saving ? "Saving…" : "Save Changes"}</RcButton>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Production Spec Modal ---------------- */
function ProductionSpecModal({ designId, catalog, onClose, onSaved }) {
  const [full, setFull] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [tracking, setTracking] = useState("");
  const [delivery, setDelivery] = useState("");
  const [placements, setPlacements] = useState([]);
  const [saving, setSaving] = useState(false);
  // Editable core fields
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [color, setColor] = useState("#000");

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/designs/${designId}/full`);
    setFull(data);
    const d = data.design;
    setStatus(d.status); setNotes(d.production_notes || "");
    setTracking(d.tracking_number || ""); setDelivery(d.estimated_delivery || "");
    setName(d.name); setPrice(d.price); setColor(d.color);
    // Seed placements from stored or suggested
    if (Array.isArray(d.work_placements) && d.work_placements.length) {
      setPlacements(d.work_placements);
    } else {
      const suggested = (data.fabric_spec?.suggested_placements || []).map((region) => ({
        region, work_type: d.embroidery, description: "", price: 0,
      }));
      setPlacements(suggested);
    }
  }, [designId]);

  useEffect(() => { load().catch(() => toast.error("Failed to load spec")); }, [load]);

  const addPlacement = () => setPlacements((p) => [...p, { region: "", work_type: "thread", description: "", price: 0 }]);
  const updatePlacement = (i, field, value) => setPlacements((p) => p.map((row, idx) => idx === i ? { ...row, [field]: value } : row));
  const removePlacement = (i) => setPlacements((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/designs/${designId}`, {
        status, production_notes: notes, tracking_number: tracking, estimated_delivery: delivery,
        work_placements: placements, name, price: parseFloat(price), color,
      });
      toast.success("Work order saved");
      onSaved(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  if (!full) {
    return (
      <div className="fixed inset-0 z-50 bg-[#120D0C]/85 backdrop-blur flex items-center justify-center" onClick={onClose}>
        <div className="text-[#A39E93]">Loading spec sheet…</div>
      </div>
    );
  }

  const d = full.design;
  const l = full.labels;
  const m = d.measurements || {};
  const fs = full.fabric_spec;
  const cost = full.manufacture_cost;

  return (
    <div className="fixed inset-0 z-50 bg-[#120D0C]/85 backdrop-blur flex items-start justify-center overflow-y-auto p-6" onClick={onClose}>
      <div className="w-full max-w-5xl bg-[#1F1816] border border-[#362B28] rounded-sm my-12" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#362B28] sticky top-0 bg-[#1F1816] z-10">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E]">Work Order</div>
            <div className="font-display text-3xl mt-1">{d.name}</div>
            <div className="text-xs text-[#A39E93] mt-1 font-mono">#{d.id.slice(0,8)}</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => window.print()} data-testid="print-spec-btn" className="text-xs tracking-[0.2em] uppercase text-[#A39E93] hover:text-[#CFA05E]">Print</button>
            <button onClick={onClose} data-testid="close-spec-modal" className="text-[#A39E93] hover:text-[#FDFBF7]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-0">
          <div className="md:col-span-2 border-r border-[#362B28]">
            {d.preview_image ? (
              <img src={d.preview_image} alt={d.name} className="w-full aspect-[3/4] object-cover" />
            ) : (
              <div className="w-full aspect-[3/4] bg-[#120D0C] flex items-center justify-center">
                <div className="w-24 h-24 rounded-full border border-[#362B28]" style={{ background: d.color }} />
              </div>
            )}
            {/* Cost breakdown card under image */}
            <div className="p-5 border-t border-[#362B28] space-y-3">
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E]">
                <DollarSign className="w-3.5 h-3.5" strokeWidth={1.5} /> Manufacture Cost
              </div>
              <CostLine label="Raw Fabric" value={cost.raw_material_cost} sub={`${fs.fabric_length_m}m × ₹${fs.fabric_cost_per_m}/m`} />
              {cost.lining_cost > 0 && <CostLine label="Lining" value={cost.lining_cost} />}
              <CostLine label="Base Labor" value={cost.base_labor_cost} />
              <CostLine label="Embroidery Labor" value={cost.embroidery_labor_cost} />
              <CostLine label="Overhead (10%)" value={cost.overhead_cost} />
              <div className="border-t border-[#362B28] pt-3">
                <CostLine label="Total Cost" value={cost.total_manufacture_cost} bold />
                <CostLine label="Selling Price" value={cost.selling_price} bold />
                <div className="flex items-baseline justify-between mt-2">
                  <div className="text-xs tracking-[0.2em] uppercase text-[#A39E93]">Margin</div>
                  <div className={`font-display text-2xl ${cost.margin >= 0 ? "text-[#CFA05E]" : "text-[#6B2737]"}`} data-testid="margin-value">
                    {formatINR(cost.margin)} <span className="text-xs">({cost.margin_pct}%)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3 p-6 space-y-6">
            {/* Editable core */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3">
                <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Design (Editable)
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Name</label>
                  <input data-testid="spec-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Selling Price (₹)</label>
                  <input type="number" step="1" data-testid="spec-price" value={price} onChange={(e) => setPrice(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input type="color" data-testid="spec-color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-12 bg-transparent border border-[#362B28]" />
                    <span className="font-mono text-xs">{color}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Customer */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3"><User className="w-3.5 h-3.5" strokeWidth={1.5} /> Customer</div>
              <div className="text-sm">
                <div>{full.customer?.name}</div>
                <div className="text-[#A39E93]">{full.customer?.email}</div>
              </div>
            </section>

            {/* Garment spec */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3"><Scissors className="w-3.5 h-3.5" strokeWidth={1.5} /> Garment Specification</div>
              <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
                <Row label="Type" value={`${l.dress_type} · ${l.dress_category}`} />
                <Row label="Fabric" value={`${l.fabric} (×${l.fabric_multiplier})`} />
                <Row label="Neckline" value={d.neck.replace("_"," ")} />
                <Row label="Sleeves" value={d.sleeve.replace("_"," ")} />
                <Row label="Length" value={d.length} />
                <Row label="Base Embroidery" value={l.embroidery} />
                <Row label="Lining" value={d.lining ? "Yes" : "No"} />
              </div>
            </section>

            {/* Fabric requirement */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3"><Package className="w-3.5 h-3.5" strokeWidth={1.5} /> Fabric Requirement</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="border border-[#362B28] bg-[#120D0C] p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93]">Length</div>
                  <div className="font-display text-2xl" data-testid="fabric-length">{fs.fabric_length_m}<span className="text-xs text-[#A39E93] ml-1">m</span></div>
                </div>
                <div className="border border-[#362B28] bg-[#120D0C] p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93]">Width</div>
                  <div className="font-display text-2xl">{fs.fabric_width_m}<span className="text-xs text-[#A39E93] ml-1">m</span></div>
                </div>
                <div className="border border-[#362B28] bg-[#120D0C] p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93]">Total Area</div>
                  <div className="font-display text-2xl">{fs.total_area_m2}<span className="text-xs text-[#A39E93] ml-1">m²</span></div>
                </div>
              </div>
            </section>

            {/* Measurements */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3"><Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Measurements (inches)</div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                {[["Bust","bust"],["Waist","waist"],["Hip","hip"],["Shoulder","shoulder"],["Sleeve","sleeve_length"],["Length","dress_length"]].map(([lbl, key]) => (
                  <div key={key} className="border border-[#362B28] bg-[#120D0C] p-3">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93]">{lbl}</div>
                    <div className="font-display text-xl">{m[key] ?? "—"}<span className="text-xs text-[#A39E93] ml-1">in</span></div>
                  </div>
                ))}
              </div>
              {m.size_preset && <div className="mt-3 text-xs text-[#A39E93]">Size preset: <span className="text-[#CFA05E]">{m.size_preset}</span></div>}
              {m.notes && (
                <div className="mt-3 p-3 border border-[#362B28] bg-[#120D0C]">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] mb-1">Customer note</div>
                  <div className="text-sm">{m.notes}</div>
                </div>
              )}
            </section>

            {/* Work placements */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E]">
                  <Scissors className="w-3.5 h-3.5" strokeWidth={1.5} /> Work Placement Detail
                </div>
                <button onClick={addPlacement} data-testid="add-placement-btn" className="text-xs tracking-[0.2em] uppercase text-[#CFA05E] hover:text-[#E6B875] inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add row
                </button>
              </div>
              <div className="border border-[#362B28] rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] bg-[#120D0C]">
                    <tr>
                      <th className="text-left p-2">Region / Placement</th>
                      <th className="text-left p-2">Work Type</th>
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2 w-24">Cost (₹)</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {placements.map((p, i) => (
                      <tr key={i} className="border-t border-[#362B28]">
                        <td className="p-1">
                          <input
                            data-testid={`placement-region-${i}`}
                            value={p.region}
                            onChange={(e) => updatePlacement(i, "region", e.target.value)}
                            placeholder="e.g. Front bodice"
                            className="w-full bg-[#120D0C] border border-transparent focus:border-[#CFA05E] outline-none px-2 py-1.5"
                          />
                        </td>
                        <td className="p-1">
                          <select
                            data-testid={`placement-type-${i}`}
                            value={p.work_type}
                            onChange={(e) => updatePlacement(i, "work_type", e.target.value)}
                            className="w-full bg-[#120D0C] border border-transparent focus:border-[#CFA05E] outline-none px-2 py-1.5 uppercase tracking-wider"
                          >
                            {catalog?.embroidery && Object.entries(catalog.embroidery).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-1">
                          <input
                            data-testid={`placement-desc-${i}`}
                            value={p.description || ""}
                            onChange={(e) => updatePlacement(i, "description", e.target.value)}
                            placeholder="Motifs, thread color, coverage…"
                            className="w-full bg-[#120D0C] border border-transparent focus:border-[#CFA05E] outline-none px-2 py-1.5"
                          />
                        </td>
                        <td className="p-1">
                          <input
                            type="number"
                            data-testid={`placement-price-${i}`}
                            value={p.price || 0}
                            onChange={(e) => updatePlacement(i, "price", parseFloat(e.target.value) || 0)}
                            className="w-full bg-[#120D0C] border border-transparent focus:border-[#CFA05E] outline-none px-2 py-1.5 text-right"
                          />
                        </td>
                        <td className="p-1 text-center">
                          <button onClick={() => removePlacement(i)} data-testid={`remove-placement-${i}`} className="text-[#A39E93] hover:text-[#6B2737]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                        </td>
                      </tr>
                    ))}
                    {placements.length === 0 && (
                      <tr><td colSpan="5" className="p-4 text-center text-[#A39E93] text-xs">No placements added. Click "Add row" to define work regions.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Production control */}
            <section>
              <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-3"><Package className="w-3.5 h-3.5" strokeWidth={1.5} /> Production Control</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Status</label>
                  <select data-testid="spec-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm uppercase tracking-wider">
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Tracking #</label>
                  <input data-testid="spec-tracking" value={tracking} onChange={(e) => setTracking(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Estimated Delivery</label>
                  <input type="date" data-testid="spec-delivery" value={delivery ? delivery.split("T")[0] : ""} onChange={(e) => setDelivery(e.target.value)} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93] block mb-1">Production Notes (internal)</label>
                  <textarea rows={3} data-testid="spec-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Fabric requisition, tailor assigned, cutting notes…" className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-[#362B28]">
                <RcButton variant="outline" onClick={onClose}>Close</RcButton>
                <RcButton onClick={save} disabled={saving} data-testid="save-spec-btn">{saving ? "Saving…" : "Save Work Order"}</RcButton>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

const CostLine = ({ label, value, sub, bold }) => (
  <div className="flex items-baseline justify-between">
    <div>
      <div className={`text-xs ${bold ? "text-[#FDFBF7]" : "text-[#A39E93]"}`}>{label}</div>
      {sub && <div className="text-[10px] text-[#A39E93]">{sub}</div>}
    </div>
    <div className={`text-sm ${bold ? "text-[#FDFBF7] font-medium" : "text-[#FDFBF7]"}`}>{formatINR(value)}</div>
  </div>
);

/* ---------------- Site Content Tab ---------------- */
function SiteContentTab({ onSaved }) {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-content").then((r) => setContent(r.data)).catch(() => toast.error("Failed to load content"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-content", content);
      setContent(data);
      toast.success("Site content updated");
      onSaved?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  if (!content) return <div className="text-[#A39E93]">Loading…</div>;

  const field = (key, label, textarea) => (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-[#A39E93] block mb-2">{label}</label>
      {textarea ? (
        <textarea rows={3} data-testid={`content-${key}`} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-4 py-3 text-sm" />
      ) : (
        <input data-testid={`content-${key}`} value={content[key] || ""} onChange={(e) => setContent({ ...content, [key]: e.target.value })} className="w-full bg-[#120D0C] border border-[#362B28] focus:border-[#CFA05E] outline-none px-4 py-3 text-sm" />
      )}
    </div>
  );

  return (
    <div className="max-w-3xl border border-[#362B28] bg-[#1F1816] p-8 rounded-sm">
      <div className="font-display text-2xl mb-2">Website Content</div>
      <p className="text-sm text-[#A39E93] mb-8 font-light">
        Edit what customers see on the homepage, header, and footer of parkcollections.co.in.
      </p>
      <div className="space-y-5">
        {field("hero_headline", "Hero Headline")}
        {field("hero_subhead", "Hero Subheading", true)}
        {field("tagline", "Brand Tagline")}
        <div className="grid sm:grid-cols-2 gap-5">
          {field("contact_email", "Contact Email")}
          {field("contact_phone", "Contact Phone")}
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {field("atelier_address", "Atelier Address")}
          {field("atelier_hours", "Atelier Hours")}
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {field("instagram", "Instagram Handle")}
          {field("whatsapp", "WhatsApp Number")}
        </div>
      </div>
      <div className="flex justify-end mt-8 pt-6 border-t border-[#362B28]">
        <RcButton onClick={save} disabled={saving} data-testid="save-content-btn">{saving ? "Saving…" : "Publish Changes"}</RcButton>
      </div>
    </div>
  );
}

/* ---------------- Main Admin ---------------- */
export default function Admin() {
  const [stats, setStats] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [orders, setOrders] = useState([]);
  const [production, setProduction] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [tab, setTab] = useState("designs");
  const [orderModal, setOrderModal] = useState(null);
  const [specModal, setSpecModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, d, o, p, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/designs"),
        api.get("/admin/orders"),
        api.get("/admin/production"),
        api.get("/catalog"),
      ]);
      setStats(s.data); setDesigns(d.data); setOrders(o.data);
      setProduction(p.data); setCatalog(c.data);
    } catch { toast.error("Failed to load admin data"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/designs/${id}/status`, { status });
      setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, status } : d)));
      toast.success("Status updated");
    } catch { toast.error("Failed to update"); }
  };

  const deleteDesign = async (id) => {
    if (!window.confirm("Delete this design and any related pending orders?")) return;
    try {
      await api.delete(`/admin/designs/${id}`);
      setDesigns((prev) => prev.filter((d) => d.id !== id));
      toast.success("Design deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const deleteOrder = async (sessionId) => {
    if (!window.confirm("Delete this order transaction? (design is untouched)")) return;
    try {
      await api.delete(`/admin/orders/${sessionId}`);
      setOrders((prev) => prev.filter((o) => o.session_id !== sessionId));
      toast.success("Order deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const onOrderSaved = (updated) => {
    setOrders((prev) => prev.map((o) => (o.session_id === updated.session_id ? { ...o, ...updated } : o)));
    setOrderModal(null);
    load();
  };
  const onSpecSaved = () => { setSpecModal(null); load(); };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="text-xs tracking-[0.3em] uppercase text-[#CFA05E] mb-2">Atelier Dashboard</div>
      <h1 className="font-display text-5xl tracking-tight mb-10">Boutique Overview</h1>

      {stats && (
        <div className="grid sm:grid-cols-4 gap-4 mb-12">
          <StatCard label="Designs" value={stats.total_designs} testid="stat-designs" />
          <StatCard label="Paid Orders" value={stats.total_paid_orders} testid="stat-paid-orders" />
          <StatCard label="Customers" value={stats.total_customers} testid="stat-customers" />
          <StatCard label="Revenue" value={formatINR(stats.revenue)} testid="stat-revenue" />
        </div>
      )}

      <div className="flex gap-6 border-b border-[#362B28] mb-6 overflow-x-auto">
        {[
          ["designs","All Designs"],
          ["orders","Orders & Payments"],
          ["production","Production Queue"],
          ["content","Site Content"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
            className={`pb-3 text-xs tracking-[0.3em] uppercase transition border-b whitespace-nowrap ${
              tab === k ? "text-[#CFA05E] border-[#CFA05E]" : "text-[#A39E93] border-transparent hover:text-[#FDFBF7]"
            }`}
          >{l}</button>
        ))}
      </div>

      {tab === "designs" && (
        <div className="border border-[#362B28] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs tracking-[0.2em] uppercase text-[#A39E93]">
              <tr className="border-b border-[#362B28]">
                <th className="text-left p-4">Design</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Fabric</th>
                <th className="text-left p-4">Price</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {designs.map((d) => (
                <tr key={d.id} className="border-b border-[#362B28]/60 hover:bg-[#1F1816]/60">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm" style={{ background: d.color }} />
                      <div>
                        <div className="font-medium">{d.name}</div>
                        <div className="text-xs text-[#A39E93]">{new Date(d.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 capitalize">{d.dress_type.replace("_", " ")}</td>
                  <td className="p-4 capitalize">{d.fabric}</td>
                  <td className="p-4 text-[#CFA05E]">{formatINR(d.price)}</td>
                  <td className="p-4">
                    <select data-testid={`status-select-${d.id}`} value={d.status} onChange={(e) => updateStatus(d.id, e.target.value)} className="bg-[#120D0C] border border-[#362B28] px-3 py-2 text-xs uppercase tracking-wider focus:border-[#CFA05E] outline-none">
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <button onClick={() => setSpecModal(d.id)} data-testid={`open-spec-${d.id}`} className="text-xs tracking-[0.2em] uppercase text-[#CFA05E] hover:text-[#E6B875]">Edit / Spec</button>
                      <button onClick={() => deleteDesign(d.id)} data-testid={`delete-design-${d.id}`} className="text-[#A39E93] hover:text-[#6B2737]" title="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {designs.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-[#A39E93]">No designs yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-[#362B28] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs tracking-[0.2em] uppercase text-[#A39E93]">
              <tr className="border-b border-[#362B28]">
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4">Design</th>
                <th className="text-left p-4">Amount</th>
                <th className="text-left p-4">Payment</th>
                <th className="text-left p-4">When</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.session_id} className="border-b border-[#362B28]/60 hover:bg-[#1F1816]/60">
                  <td className="p-4">{o.user_email}</td>
                  <td className="p-4">{o.design?.name || "—"}</td>
                  <td className="p-4 text-[#CFA05E]">{formatINR(o.amount)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-[10px] tracking-[0.2em] uppercase border ${
                      o.payment_status === "paid" ? "border-[#CFA05E] text-[#CFA05E]" :
                      o.payment_status === "refunded" ? "border-[#6B2737] text-[#6B2737]" :
                      "border-[#362B28] text-[#A39E93]"
                    }`}>{o.payment_status}</span>
                  </td>
                  <td className="p-4 text-[#A39E93] text-xs">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <button onClick={() => setOrderModal(o)} data-testid={`open-order-${o.session_id}`} className="text-xs tracking-[0.2em] uppercase text-[#CFA05E] hover:text-[#E6B875]">Modify</button>
                      <button onClick={() => deleteOrder(o.session_id)} data-testid={`delete-order-${o.session_id}`} className="text-[#A39E93] hover:text-[#6B2737]" title="Delete"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan="6" className="p-8 text-center text-[#A39E93]">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "production" && (
        <div>
          <div className="mb-4 text-sm text-[#A39E93]">
            <span className="text-[#CFA05E] font-medium">{production.length}</span> garment{production.length !== 1 && "s"} currently in production. Click any card to open the full work order with fabric, cost breakdown and placement details.
          </div>
          {production.length === 0 ? (
            <div className="text-center py-24 border border-dashed border-[#362B28] rounded-sm">
              <div className="font-display text-3xl mb-3">Nothing in production.</div>
              <p className="text-[#A39E93] font-light">Designs move here automatically after a customer completes payment.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {production.map((d) => (
                <button key={d.id} onClick={() => setSpecModal(d.id)} data-testid={`production-card-${d.id}`}
                  className="text-left border border-[#362B28] bg-[#1F1816] hover:border-[#CFA05E]/60 transition rounded-sm overflow-hidden group"
                >
                  <div className="aspect-[3/2] relative overflow-hidden bg-[#120D0C]">
                    {d.preview_image ? (
                      <img src={d.preview_image} alt={d.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full border border-[#362B28]" style={{ background: d.color }} />
                      </div>
                    )}
                    <div className="absolute top-3 right-3 px-2 py-1 text-[10px] tracking-[0.2em] uppercase bg-[#120D0C]/80 border border-[#362B28] text-[#CFA05E] backdrop-blur">{d.status.replace("_"," ")}</div>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="font-display text-xl">{d.name}</div>
                    <div className="text-xs text-[#A39E93] tracking-wider uppercase">{d.dress_type.replace("_"," ")} · {d.fabric} · {d.embroidery.replace("_"," ")}</div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#362B28]">
                      <div className="text-xs text-[#A39E93]">Size {d.measurements?.size_preset || "custom"}</div>
                      <div className="text-[#CFA05E]">{formatINR(d.price)}</div>
                    </div>
                    {d.tracking_number && (
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#A39E93]">Track: <span className="text-[#FDFBF7] font-mono">{d.tracking_number}</span></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "content" && <SiteContentTab onSaved={() => {}} />}

      {orderModal && <OrderModal order={orderModal} onClose={() => setOrderModal(null)} onSaved={onOrderSaved} />}
      {specModal && <ProductionSpecModal designId={specModal} catalog={catalog} onClose={() => setSpecModal(null)} onSaved={onSpecSaved} />}
    </div>
  );
}
